import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { BlogSubscribeBox } from '../components/BlogSubscribeBox';
import { BLOG_POSTS, BLOG_TAGS, BLOG_AUTHOR } from '../data/blogPosts';

const fmtDate = (d) => new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

function PostCard({ p, featured = false }) {
  return (
    <Link
      to={`/blog/${p.slug}`}
      className={`block bg-white border border-[#E2E2E2] rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all ${featured ? 'p-8 md:p-10' : 'p-6'}`}
      data-testid={`blog-card-${p.slug}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-[#D4AF37]">{p.tag}</span>
        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {p.readMins} min read</span>
      </div>
      <h3 className={`font-semibold text-[#0F392B] leading-snug mb-2 ${featured ? 'text-2xl md:text-3xl' : 'text-lg'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
        {p.title}
      </h3>
      <p className={`text-muted-foreground leading-relaxed ${featured ? 'text-sm max-w-2xl' : 'text-[13px] line-clamp-3'}`}>{p.description}</p>
      <div className="flex items-center justify-between mt-4">
        <p className="text-[11px] text-muted-foreground">{BLOG_AUTHOR.name} · {fmtDate(p.date)}</p>
        <span className="text-xs font-medium text-[#0F392B] inline-flex items-center gap-1">Read <ArrowRight className="h-3 w-3" /></span>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const [tag, setTag] = useState(null);
  const posts = useMemo(() => (tag ? BLOG_POSTS.filter((p) => p.tag === tag) : BLOG_POSTS), [tag]);
  const [featured, ...rest] = posts;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "The New Ivy Playbook",
    "url": "https://ivyleagueconsulting.org/blog",
    "author": { "@type": "Person", "name": BLOG_AUTHOR.name, "jobTitle": BLOG_AUTHOR.role },
    "blogPost": BLOG_POSTS.map((p) => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "datePublished": p.date,
      "url": `https://ivyleagueconsulting.org/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="blog-page">
      <SEO
        title="The New Ivy Playbook — Essays on Admissions, AI & Transfer Strategy"
        description="Collected essays by Taimur Ahmed, Founder & CEO of Ivy League Consulting: the new rules of AI-era admissions, transfer strategy, community college launchpads, and honest advice on prestige, rankings, and cost."
        keywords="college admissions blog, ivy league admissions advice, transfer strategy blog, ai college admissions essays, community college to ivy league"
        schema={schema}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-16 bg-[#0F392B] text-white">
        <div className="max-w-5xl mx-auto px-4 py-16 md:py-20 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4 flex items-center justify-center gap-2">
            <BookOpen className="h-4 w-4" /> Collected essays
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            The New Ivy <span className="italic text-[#D4AF37]">Playbook</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Essays on college admissions, AI, transfer strategy, and the American education story — written by our founder, not a content farm.
          </p>
        </div>
      </section>

      {/* Author strip */}
      <section className="bg-white border-b border-[#E2E2E2]" data-testid="blog-author-strip">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#0F392B] text-[#D4AF37] flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>TA</div>
          <div>
            <p className="text-sm font-semibold text-[#0F392B]">{BLOG_AUTHOR.name} <span className="font-normal text-muted-foreground">— {BLOG_AUTHOR.role}</span></p>
            <p className="text-xs text-muted-foreground leading-relaxed mt-1 max-w-2xl">{BLOG_AUTHOR.bio}</p>
          </div>
        </div>
      </section>

      {/* Tag filter + posts */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-tag-filters">
            <button type="button" onClick={() => setTag(null)} data-testid="blog-tag-all"
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide border transition-all ${!tag ? 'bg-[#0F392B] text-white border-[#0F392B]' : 'bg-white text-[#0F392B] border-[#E2E2E2] hover:border-[#0F392B]/40'}`}>
              All essays ({BLOG_POSTS.length})
            </button>
            {BLOG_TAGS.map((t) => (
              <button key={t} type="button" onClick={() => setTag(t === tag ? null : t)} data-testid={`blog-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide border transition-all ${tag === t ? 'bg-[#0F392B] text-white border-[#0F392B]' : 'bg-white text-[#0F392B] border-[#E2E2E2] hover:border-[#0F392B]/40'}`}>
                {t}
              </button>
            ))}
          </div>

          {featured && (
            <div className="mb-6" data-testid="blog-featured">
              <PostCard p={featured} featured />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="blog-grid">
            {rest.map((p) => <PostCard key={p.slug} p={p} />)}
          </div>

          <div className="mt-10" data-testid="blog-hub-subscribe">
            <BlogSubscribeBox source="hub" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="blog-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Reading is good. A strategy is better.
          </h2>
          <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
            Take the free 12-question diagnostic and see exactly where your application stands across six Ivy signals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/diagnostic">
              <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold shadow-xl" data-testid="blog-cta-diagnostic">
                Take the Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/consulting">
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-[#0F392B]" data-testid="blog-cta-consulting">
                Explore Consulting
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
